import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Footer from '../components/footer'
import Breadcrumbs from '../components/breadcrumbs'
import { getCanonicalUrl } from '../utils/canonical'
import './about.css'

const About = () => {
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'About' }
  ]

  return (
    <div className="about-container">
      <Helmet>
        <title>About DataFlows | Australian SMS Marketing Platform</title>
        <meta
          name="description"
          content="DataFlows is an Australian SMS marketing and automation platform built for businesses that want a simpler, more direct way to communicate with their customers."
        />
        <link rel="canonical" href={getCanonicalUrl('/about')} />
        <meta property="og:title" content="About DataFlows | Australian SMS Marketing Platform" />
        <meta property="og:description" content="DataFlows is an Australian SMS marketing and automation platform built for businesses that want a simpler, more direct way to communicate with their customers." />
        <meta property="og:url" content="https://dataflows.com.au/about" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "url": "https://dataflows.com.au/about",
          "name": "About DataFlows",
          "description": "DataFlows is an Australian SMS marketing and automation platform built for businesses that want a simpler, more direct way to communicate with their customers.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
              { "@type": "ListItem", "position": 2, "name": "About", "item": "https://dataflows.com.au/about" }
            ]
          }
        })}</script>
      </Helmet>

      <header className="about-header">
        <Link to="/" className="about-logo-link">
          <img
            alt="DataFlows Logo"
            src="/dataflows-high-resolution-logo-200h.png"
            className="about-logo"
          />
        </Link>
        <nav className="about-nav">
          <Link to="/" className="about-nav-link">Home</Link>
          <Link to="/blog" className="about-nav-link">Blog</Link>
          <Link to="/developers" className="about-nav-link">Developers</Link>
          <Link to="/contact-us" className="about-nav-link">Contact</Link>
        </nav>
      </header>

      <div className="about-breadcrumbs">
        <div className="about-breadcrumbs-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <section className="about-hero">
        <div className="about-hero-inner">
          <p className="about-hero-label">About Us</p>
          <h1 className="about-hero-title">Built in Australia, for Australian businesses</h1>
          <p className="about-hero-subtitle">
            DataFlows started with a straightforward idea: SMS is one of the most reliable ways
            to reach a customer, and it should be easy for any business to use it well.
          </p>
        </div>
      </section>

      <section className="about-story">
        <div className="about-section-inner">
          <div className="about-story-content">
            <h2>Our story</h2>
            <p>
              We built DataFlows because we kept seeing the same problem. Businesses knew SMS worked,
              open rates are consistently above 90%, yet the tools available were either too expensive,
              too complicated, or not built with Australian compliance requirements in mind.
            </p>
            <p>
              So we built something that fixes that. DataFlows is a platform designed around the
              workflows that Australian businesses actually use, from appointment reminders and
              promotional campaigns to two-factor authentication and automated notifications.
              Everything is built to comply with the Spam Act 2003 and ACMA guidelines from
              the ground up, not as an afterthought.
            </p>
            <p>
              We are a proudly Australian company, and our platform is used by businesses across
              healthcare, retail, real estate, hospitality, and professional services throughout
              Australia.
            </p>
          </div>
        </div>
      </section>

      <section className="about-values">
        <div className="about-section-inner">
          <h2 className="about-section-title">What we stand for</h2>
          <div className="about-values-grid">
            <div className="about-value-card">
              <h3>Simplicity</h3>
              <p>
                Sending a bulk SMS campaign or setting up an automated workflow should not require
                a developer or a week of onboarding. We keep the platform straightforward so your
                team can get going on day one.
              </p>
            </div>
            <div className="about-value-card">
              <h3>Reliability</h3>
              <p>
                When you send an SMS, it needs to arrive. We work with carrier-grade infrastructure
                and provide real-time delivery tracking so you always know the status of every message.
              </p>
            </div>
            <div className="about-value-card">
              <h3>Compliance</h3>
              <p>
                Australian SMS law is specific about consent, opt-outs, and sender identification.
                DataFlows handles the technical side of compliance so you can focus on your
                messaging without worrying about the fine print.
              </p>
            </div>
            <div className="about-value-card">
              <h3>Integration</h3>
              <p>
                Your business already uses tools you rely on. DataFlows connects with Zapier,
                Microsoft Power Automate, Microsoft Teams, Auth0, Supabase, and your email client,
                so SMS fits into your existing workflow rather than replacing it.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-platform">
        <div className="about-section-inner">
          <h2 className="about-section-title">The platform</h2>
          <p className="about-platform-intro">
            DataFlows gives you everything you need to run SMS communication at any scale,
            whether you are a small business sending a few hundred messages a month or a larger
            operation running high-volume automated campaigns.
          </p>
          <div className="about-features-grid">
            <div className="about-feature-item">
              <h4>Bulk SMS</h4>
              <p>Send personalised messages to your entire contact list in seconds, with branded sender IDs so recipients know exactly who the message is from.</p>
            </div>
            <div className="about-feature-item">
              <h4>SMS API</h4>
              <p>Integrate SMS directly into your application with our REST API. Send transactional messages, delivery confirmations, and notifications programmatically.</p>
            </div>
            <div className="about-feature-item">
              <h4>Two-Factor Authentication</h4>
              <p>Add an extra layer of security to your platform with SMS-based OTP verification. We are an official Auth0 Marketplace partner for SMS MFA.</p>
            </div>
            <div className="about-feature-item">
              <h4>Automated Reminders</h4>
              <p>Reduce no-shows and keep customers informed with scheduled SMS reminders for appointments, bookings, and deadlines.</p>
            </div>
            <div className="about-feature-item">
              <h4>Workflow Automation</h4>
              <p>Connect SMS to over 6,000 apps through Zapier or build complex automated workflows in Microsoft Power Automate without writing a line of code.</p>
            </div>
            <div className="about-feature-item">
              <h4>Analytics</h4>
              <p>Track delivery rates, open windows, and campaign performance in real time so you can see what is working and improve over time.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-trust">
        <div className="about-section-inner">
          <h2 className="about-section-title">Trusted partnerships</h2>
          <p>
            DataFlows is an official partner on the Auth0 Marketplace, listed on Microsoft AppSource,
            and a published integration on Zapier. These partnerships mean our platform has been
            reviewed and verified by the organisations that built the tools you already use.
          </p>
          <div className="about-badges">
            <div className="about-badge">Official Auth0 Marketplace Partner</div>
            <div className="about-badge">Microsoft AppSource Listed</div>
            <div className="about-badge">Zapier Integration</div>
            <div className="about-badge">ACMA Compliant</div>
          </div>
        </div>
      </section>

      <section className="about-cta">
        <div className="about-section-inner">
          <h2>Ready to get started?</h2>
          <p>
            Pay-as-you-go from $35 AUD, with no monthly fees, no lock-in contracts and no setup fees. Create an account
            and send your first SMS in minutes.
          </p>
          <div className="about-cta-buttons">
            <a
              href="https://sms.dataflows.com.au/register"
              target="_blank"
              rel="noopener noreferrer"
              className="about-cta-primary"
            >
              Create a free account
            </a>
            <Link to="/contact-us" className="about-cta-secondary">
              Talk to us first
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default About
